import { createBrowserRouter, Outlet, useLoaderData } from 'react-router';
import type { LoaderFunctionArgs } from 'react-router';
import { NotFound } from '../ui';
import { getMyInstance } from '../shared/queries';
import { supabase } from '../shared/supabase/client';
import { InstanceProvider, loadPublicInstance } from './instance/InstanceProvider';
import { DemoDataMarker } from './DemoDataMarker';
import { useInstanceApp } from './pwa';
import type { PublicInstance } from './instance/InstanceProvider';
import { AdminLayout } from './admin/AdminLayout';
import { SignInPage } from './admin/SignInPage';
import { RosterPage } from './admin/RosterPage';
import { ClientPlanPage } from './admin/ClientPlanPage';
import { TemplatesPage } from './admin/TemplatesPage';
import { ClientHome } from './client/ClientHome';
import { WelcomePage } from './client/WelcomePage';
import { TodayPage } from './client/TodayPage';
import { WeekPage } from './client/WeekPage';
import { SessionDetailPage } from './client/SessionDetailPage';
import { CheckInPage } from './client/CheckInPage';
import { NotePage, NotesPage } from './client/NotesPage';
import { VideosPage } from './client/VideosPage';
import { MessagesPage } from './client/MessagesPage';
import { StartPage } from './client/StartPage';
import { MePage } from './client/MePage';
import { MeasurementsPage } from './client/MeasurementsPage';
import { ClientLoading, ClientNotFound } from './client/ClientPage';

/**
 * Two route trees: `/admin/*` for the coach and `/c/:slug/*` for her clients.
 *
 * The slug is resolved here, in the loader, and **nowhere else**. Three layers
 * keep one instance out of another (design.md F5), and only the third is
 * security:
 *
 *   1. this loader renders not-found for an unknown or inactive slug;
 *   2. once there is a session, a slug that is not the caller's instance also
 *      renders **not found rather than redirecting to the right one** — a
 *      redirect would confirm that the other instance exists;
 *   3. no data query anywhere takes the slug as a parameter. Row-level security
 *      supplies the tenant filter, so editing the address returns zero rows even
 *      if layers 1 and 2 were both deleted.
 *
 * Layer 3 is why this file resolves branding and nothing else.
 */

async function instanceLoader({ params }: LoaderFunctionArgs): Promise<PublicInstance | null> {
  const slug = params['slug'];
  if (!slug) return null;

  // Layer two, before layer one is even asked.
  //
  // A signed-in person on somebody else's slug learns nothing at all — not even
  // that the instance exists — because the branding lookup is never made. An
  // anonymous visitor still resolves it: an instance's name and coach line are
  // public to anyone with the address, by design (D19, design.md F4). What must
  // never happen is one instance's client being shown another instance's name.
  const { data } = await supabase.auth.getSession();
  if (data.session) {
    const mine = await getMyInstance(supabase);
    if (mine?.slug !== slug) return null;
  }

  return loadPublicInstance(slug);
}

/**
 * The client shell: one instance's branding, the demo marker, and whichever
 * screen the address names. Exported so `DemoDataMarker.test.tsx` can mount it
 * and prove the marker is there — a shell nobody can render is a shell nobody
 * can check.
 */
export function InstanceShell() {
  const instance = useLoaderData<PublicInstance | null>();

  // The manifest and the service worker, both scoped to this instance's path
  // and to nothing wider. A no-op when the slug did not resolve, so a surface
  // that renders as not found installs nothing.
  useInstanceApp(instance?.slug ?? null);

  // One answer for "unknown slug", "suspended instance" and "not yours".
  if (!instance) return <ClientNotFound />;

  return (
    <InstanceProvider instance={instance}>
      {/*
        Above the outlet, so it is on every client screen without any of them
        having to remember it — `demo-data` requires the marker on *every*
        screen, and a thing each screen opts into is a thing a new screen
        forgets. It renders nothing unless this build was deliberately
        configured for invented data.
      */}
      <DemoDataMarker />
      <Outlet />
    </InstanceProvider>
  );
}

export const router = createBrowserRouter([
  {
    path: '/admin/sign-in',
    Component: SignInPage,
  },
  {
    path: '/admin',
    Component: AdminLayout,
    children: [
      { index: true, Component: RosterPage },
      { path: 'clients/:clientId', Component: ClientPlanPage },
      { path: 'templates', Component: TemplatesPage },
    ],
  },
  {
    path: '/c/:slug',
    loader: instanceLoader,
    Component: InstanceShell,
    // A failure resolving the slug must look like every other failure — and
    // says so in Castilian, like every other word on a client screen.
    ErrorBoundary: ClientNotFound,
    // Shown while the loader resolves the slug, on the first paint.
    HydrateFallback: ClientLoading,
    children: [
      { index: true, Component: ClientHome },
      { path: 'welcome', Component: WelcomePage },
      { path: 'today', Component: TodayPage },
      /*
       * The week, and the weeks around it. Two routes rather than one optional
       * segment so that `/week` always means "the one I am in" and
       * `/week/<n>` always means a particular one — which is what lets a client
       * go back three weeks, open a session and come back to where they were.
       *
       * The number in the address is a week *number*, never an id. A number the
       * client may not see renders as not found, exactly as a foreign week does;
       * the one number that gets its own answer is the week after the last
       * published one, and that answer says the coach is still preparing it. See
       * `WeekPage.tsx`.
       */
      { path: 'week', Component: WeekPage },
      { path: 'week/:weekNumber', Component: WeekPage },
      { path: 'sessions/:sessionId', Component: SessionDetailPage },
      { path: 'sessions/:sessionId/check-in', Component: CheckInPage },
      /*
       * The three screens where the client meets the person coaching them.
       *
       * They hang off the instance shell like every other client route, so they
       * inherit the branding, the demo marker and the not-found answer, and each
       * of them is behind `ClientGuard` in its own file. There is still **no tab
       * bar**: the hi-fi has three tabs, `mvp-discovery` describes five, and that
       * disagreement is the owner's to settle. Today carries the entry points in
       * the meantime — see `TodayPage.tsx`.
       */
      { path: 'notes', Component: NotesPage },
      { path: 'notes/:noteId', Component: NotePage },
      { path: 'videos', Component: VideosPage },
      { path: 'messages', Component: MessagesPage },
      /*
       * The way in, and what the product holds about the client.
       *
       * `start` is the acknowledgement, and it is the one client route that is
       * **not** behind the acknowledgement gate — it is what collects it. Every
       * other route here is, in `ClientGuard`, in one place, so a screen added
       * later cannot forget it.
       *
       * `me` is the fifth and last destination `mvp-discovery` → `client-app`
       * describes, and **there is still no tab bar**. The hi-fi has three tabs,
       * the specs describe five, and `client-me`'s own spec says nothing about
       * navigation — so the entry point goes where the other four put theirs, on
       * Today, and the question stays the owner's. Every destination now exists,
       * which is the thing that was missing when it was first raised.
       */
      { path: 'start', Component: StartPage },
      { path: 'me', Component: MePage },
      { path: 'measurements', Component: MeasurementsPage },
    ],
  },
  {
    path: '*',
    Component: NotFound,
  },
]);
